/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { BreakpointQueryName } from '@doveit/lego';
import React, {
  Children, cloneElement, FunctionComponent, ReactElement, ReactNode, useCallback, useMemo, useState,
} from 'react';
import { useMediaQuery } from 'styled-breakpoints/use-media-query';
import { useTheme } from 'styled-components';
import HAccordionBlock, { HAccordionBlockProps } from './components/block/HAccordionBlock';
import * as styles from './HAccordion.style';
import HAccordionContext from './HAccordionContext';

export interface HAccordionProps {
  initialOpenBlockPosition?: number,
  blockHeight?: string,
  children: ReactNode,
}

export type HAccordionComposition = {
  Block: FunctionComponent<HAccordionBlockProps>,
};

export const DEFAULT_BLOCK_HEIGHT = '25rem';

const HAccordion: FunctionComponent<HAccordionProps> & HAccordionComposition = ({
  initialOpenBlockPosition,
  blockHeight = DEFAULT_BLOCK_HEIGHT,
  children,
}) => {
  const [openBlockPosition, setBlockOpenPosition] = useState<number | undefined>(initialOpenBlockPosition);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BreakpointQueryName.MD));

  const updateOpenBlockPosition = useCallback(
    (current) => () => setBlockOpenPosition(current),
    [],
  );

  const contextValue = useMemo(() => {
    const total = Children.toArray(children).length;

    if (total <= 1) {
      throw new Error('Provide a minimum of two HAccordion.Block');
    }

    return ({
      // It indicates that the last open card will be open after the mouse leaves the interactive component area
      showOnMouseLeave: initialOpenBlockPosition !== undefined,
      total,
      openBlockPosition,
      blockHeight,
      updateOpenBlockPosition,
      isMobile,
    });
  }, [children, initialOpenBlockPosition, openBlockPosition, isMobile, blockHeight, updateOpenBlockPosition]);

  return (
    <ul css={styles.base}>
      <HAccordionContext.Provider value={contextValue}>
        {Children.map(children, (child: ReactElement, i: number) => {
          if ((child.type !== HAccordionBlock)) {
            throw new Error('The component children must be an array of HAccordion.Block');
          }

          return cloneElement(child, {
            position: i + 1,
          });
        })}
      </HAccordionContext.Provider>
    </ul>
  );
};

HAccordion.Block = HAccordionBlock;

export default HAccordion;
